/**
 * Extracts month key (YYYY-MM) from a date string (YYYY-MM-DD or YYYY-MM format)
 */
export const getMonthKey = (dateStr: string | null): string | null => {
  if (!dateStr || typeof dateStr !== "string") return null;
  return dateStr.substring(0, 7);
};

/**
 * Extracts year (YYYY) from a date string (YYYY-MM-DD or YYYY-MM format)
 */
export const getYearKey = (dateStr: string | null): string | null => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const match = dateStr.match(/^(\d{4})/);
  return match ? match[1] : null;
};

/**
 * Normalizes data by aggregating a date field to month or year format.
 * Creates a new array where the date field values are replaced with their aggregated key.
 * 
 * @param data - Raw data array
 * @param dateField - The date field name to normalize
 * @param dateAggregation - Can be:
 *   - A string: "month" (YYYY-MM) or "year" (YYYY)
 *   - An array: ["year", "cost", "count"] where first element is granularity and rest are fields to sum
 * @returns New array with normalized date field and aggregated sum fields, or original data if no aggregation specified
 * 
 * @example
 * normalizeDataByDateField(data, "purchase_date", "year")
 * // Transforms: [{ purchase_date: "2024-01-15", type: "A" }]
 * // To:         [{ purchase_date: "2024", type: "A" }]
 * 
 * @example
 * normalizeDataByDateField(data, "purchase_date", ["year", "cost", "count"])
 * // Groups by year and sums cost and count
 */
export const normalizeDataByDateField = (
  data: Record<string, any>[],
  dateField: string,
  dateAggregation?: "month" | "year" | ["month" | "year", ...string[]],
  groupDimensions?: readonly string[]
): Record<string, any>[] => {
  if (!dateAggregation || !dateField) {
    return data;
  }

  const granularity = Array.isArray(dateAggregation) ? dateAggregation[0] : dateAggregation;
  const fieldsToSum = Array.isArray(dateAggregation) ? dateAggregation.slice(1) : [];

  // First normalize the date
  let normalized = data.map((item) => ({
    ...item,
    [dateField]:
      granularity === "year"
        ? getYearKey(item[dateField]) || item[dateField]
        : getMonthKey(item[dateField]) || item[dateField],
  }));

  // Only collapse rows into groups when the caller explicitly provides groupDimensions.
  // Without it (e.g. multi-line charts), just normalise the date values and return —
  // downstream aggregation in aggregateForChart handles grouping by secondary dimension.
  if (groupDimensions !== undefined) {
    // Auto-detect numeric fields to sum when none are explicitly listed in the config
    // (e.g. dateAggregation: ["year"] with no extra fields after the granularity).
    const effectiveFieldsToSum =
      fieldsToSum.length > 0
        ? fieldsToSum
        : normalized.length > 0
          ? Object.keys(normalized[0]).filter(
              (k) => k !== dateField && typeof normalized[0][k] === "number"
            )
          : [];

    const grouped = new Map<string, Record<string, any>>();

    // Build the grouping key from dimension fields only — exclude summed fields,
    // otherwise every row is unique and nothing collapses.
    const keyDimensions =
      groupDimensions.length > 0
        ? groupDimensions.filter((d) => !effectiveFieldsToSum.includes(d))
        : [];

    normalized.forEach((item) => {
      const key =
        keyDimensions.length > 0
          ? keyDimensions.map((d) => item[d]).join("|")
          : String(item[dateField]);

      if (!grouped.has(key)) {
        grouped.set(key, { ...item });
      } else {
        const existing = grouped.get(key)!;
        effectiveFieldsToSum.forEach((field) => {
          if (
            typeof existing[field] === "number" &&
            typeof item[field] === "number"
          ) {
            existing[field] = (existing[field] || 0) + item[field];
          }
        });
      }
    });

    normalized = Array.from(grouped.values());
  }

  return normalized;
};

/**
 * Detects if a field name or value looks like a date field.
 * Checks field name for common patterns (date, purchase_date, created, etc.)
 * and validates sample values against date format patterns.
 * 
 * @param fieldName - The field name to check
 * @param sampleValue - Optional sample value to validate format
 * @returns true if field appears to be a date field
 */
export const isDateField = (fieldName: string, sampleValue?: any): boolean => {
  // Check field name for common date patterns
  const datePatterns = /date|time|year|month|quarter|period/i;
  if (datePatterns.test(fieldName)) {
    return true;
  }
  
  // If we have a sample value, check the format
  // Reject plain numbers — sum fields (cost, count) can be 4-digit integers that
  // superficially match /^\d{4}/ but are not dates.
  if (sampleValue !== null && sampleValue !== undefined && typeof sampleValue !== "number") {
    const valueStr = String(sampleValue);
    // Require at least YYYY-MM format (with a dash) to avoid matching bare integers like 1500
    if (/^\d{4}-\d{2}/.test(valueStr)) {
      return true;
    }
  }
  
  return false;
};

/**
 * Auto-detects which dimension is a date field.
 * If dateField is explicitly provided, uses that.
 * Otherwise, tries to find a date field in the dimensions array.
 * 
 * @param dimensions - Array of dimension field names
 * @param dateField - Optional explicit date field name
 * @param data - Optional sample data to validate format
 * @returns The detected date field name, or undefined if not found
 */
export const detectDateField = (
  dimensions: readonly string[],
  dateField?: 'month' | 'year' | string,
  data?: Record<string, any>[]
): string | undefined => {
  // If explicit dateField provided, use it
  if (dateField) {
    return dateField;
  }
  
  // Try to find a date field in dimensions
  const sampleRow = data?.[0];
  for (const dim of dimensions) {
    if (isDateField(dim, sampleRow?.[dim])) {
      return dim;
    }
  }
  
  return undefined;
};
